import type { VoyagerDetailItem } from '../models/detail-item';

export interface MetadataRow {
  label: string;
  value: string | number;
  isLink?: boolean;
}

type BaseDescriptor = { label: string; isLink?: boolean };

type StringField = BaseDescriptor & {
  type: 'string';
  key: keyof VoyagerDetailItem;
};
type NumberField = {
  type: 'number';
  key: keyof VoyagerDetailItem;
  label: string;
};
type DateField = {
  type: 'date';
  key: keyof VoyagerDetailItem;
  label: string;
};
type BooleanField = {
  type: 'boolean';
  key: keyof VoyagerDetailItem;
  label: string;
};
type ArrayField = BaseDescriptor & {
  type: 'array';
  key: keyof VoyagerDetailItem;
};
type CompositeStringField = BaseDescriptor & {
  type: 'composite';
  keys: (keyof VoyagerDetailItem)[];
};
type CompositeDateField = {
  type: 'composite-date';
  keys: (keyof VoyagerDetailItem)[];
  label: string;
};

type MetadataFieldDescriptor =
  | StringField
  | NumberField
  | DateField
  | BooleanField
  | ArrayField
  | CompositeStringField
  | CompositeDateField;

export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString;
  }
};

const METADATA_FIELD_DESCRIPTORS: MetadataFieldDescriptor[] = [
  {
    type: 'composite',
    keys: ['fullpath', 'absolute_path'],
    label: 'Path',
    isLink: true,
  },
  {
    type: 'composite-date',
    keys: ['fd__acquired', 'fd_acquired'],
    label: 'Acquired',
  },
  { type: 'number', key: 'fi__anomalous_pixels', label: 'Anomalous Pixels' },
  {
    type: 'number',
    key: 'fi__clear_confidence_percent',
    label: 'Clear Confidence Percent',
  },
  { type: 'number', key: 'fi__clear_percent', label: 'Clear Percent' },
  { type: 'number', key: 'fi__cloud_cover', label: 'Cloud Cover' },
  { type: 'number', key: 'fi__cloud_percent', label: 'Cloud Percent' },
  { type: 'boolean', key: 'fb__ground_control', label: 'Ground Control' },
  { type: 'number', key: 'fi__gsd', label: 'Gsd' },
  {
    type: 'number',
    key: 'fi__heavy_haze_percent',
    label: 'Heavy Haze Percent',
  },
  { type: 'string', key: 'fs__instrument', label: 'Instrument', isLink: true },
  { type: 'string', key: 'fs__item_type', label: 'Item Type', isLink: true },
  {
    type: 'number',
    key: 'fi__light_haze_percent',
    label: 'Light Haze Percent',
  },
  { type: 'number', key: 'fi__pixel_resolution', label: 'Pixel Resolution' },
  { type: 'string', key: 'fs__provider', label: 'Provider', isLink: true },
  { type: 'date', key: 'fd__published', label: 'Published' },
  {
    type: 'string',
    key: 'fs__publishing_stage',
    label: 'Publishing Stage',
    isLink: true,
  },
  { type: 'string', key: 'fs__quality_category', label: 'Quality Category' },
  {
    type: 'number',
    key: 'fi__satellite_azimuth',
    label: 'Satellite Azimuth',
  },
  { type: 'string', key: 'fs__satellite_id', label: 'Satellite Id' },
  { type: 'number', key: 'fi__shadow_percent', label: 'Shadow Percent' },
  { type: 'number', key: 'fi__snow_ice_percent', label: 'Snow Ice Percent' },
  { type: 'string', key: 'fs__strip_id', label: 'Strip Id', isLink: true },
  { type: 'number', key: 'fu__sun_azimuth', label: 'Sun Azimuth' },
  { type: 'number', key: 'fu__sun_elevation', label: 'Sun Elevation' },
  { type: 'date', key: 'fd__updated', label: 'Updated' },
  { type: 'number', key: 'fu__view_angle', label: 'View Angle' },
  {
    type: 'number',
    key: 'fi__visible_confidence_percent',
    label: 'Visible Confidence Percent',
  },
  { type: 'number', key: 'fi__visible_percent', label: 'Visible Percent' },
  {
    type: 'string',
    key: 'fs_thumbnail_url',
    label: 'Thumbnail Url',
    isLink: true,
  },
  { type: 'string', key: 'agent', label: 'Agent', isLink: true },
  {
    type: 'string',
    key: 'fs_agent_extract',
    label: 'Agent Extract',
    isLink: true,
  },
  { type: 'boolean', key: 'has_spatial', label: 'Has Spatial' },
  { type: 'array', key: 'grp_Agency', label: 'Agency', isLink: true },
  {
    type: 'array',
    key: 'grp_Organization',
    label: 'Organization',
    isLink: true,
  },
  { type: 'array', key: 'grp_Sector', label: 'Sector', isLink: true },
  {
    type: 'array',
    key: 'grp_Data_Theme',
    label: 'Data Theme',
    isLink: true,
  },
  { type: 'date', key: 'created', label: 'Created' },
  { type: 'array', key: 'grp_Catalog', label: 'Catalog', isLink: true },
  { type: 'string', key: 'fs_source', label: 'Source', isLink: true },
  {
    type: 'array',
    key: 'grp_Collection',
    label: 'Collection',
    isLink: true,
  },
  {
    type: 'array',
    key: 'grp_data_source',
    label: 'Data Source',
    isLink: true,
  },
  {
    type: 'array',
    key: 'grp_Payment_Required',
    label: 'Payment Required',
    isLink: true,
  },
  {
    type: 'string',
    key: 'format_company',
    label: 'Format Company',
    isLink: true,
  },
  {
    type: 'string',
    key: 'format_category',
    label: 'Format Category',
    isLink: true,
  },
  {
    type: 'string',
    key: 'format_type',
    label: 'Format Type',
    isLink: true,
  },
  { type: 'string', key: 'location', label: 'Location', isLink: true },
  { type: 'number', key: 'area', label: 'Area' },
  { type: 'string', key: 'bbox', label: 'Bbox' },
  { type: 'date', key: 'indexed', label: 'Last Indexed' },
  { type: 'string', key: 'name', label: 'Name' },
  {
    type: 'string',
    key: 'fs_source_location',
    label: 'Source Location',
    isLink: true,
  },
  { type: 'string', key: 'fs_vendor', label: 'Vendor', isLink: true },
  { type: 'string', key: 'fs_platform', label: 'Platform', isLink: true },
  { type: 'array', key: 'properties', label: 'Properties', isLink: true },
  { type: 'number', key: 'views', label: 'Views' },
  { type: 'number', key: 'interactions', label: 'Interactions' },
  {
    type: 'string',
    key: 'absolute_path',
    label: 'Absolute Path',
    isLink: true,
  },
  { type: 'number', key: 'likes', label: 'Likes' },
  { type: 'number', key: 'dislikes', label: 'Dislikes' },
];

export const getMetadataRows = (item: VoyagerDetailItem): MetadataRow[] =>
  METADATA_FIELD_DESCRIPTORS.flatMap((descriptor): MetadataRow[] => {
    switch (descriptor.type) {
      case 'composite': {
        const value = descriptor.keys.map((k) => item[k]).find(Boolean);
        if (!value) return [];
        return [
          {
            label: descriptor.label,
            value: String(value),
            isLink: descriptor.isLink,
          },
        ];
      }
      case 'composite-date': {
        const value = descriptor.keys.map((k) => item[k]).find(Boolean);
        if (!value) return [];
        return [{ label: descriptor.label, value: formatDate(String(value)) }];
      }
      case 'string': {
        const value = item[descriptor.key];
        if (!value) return [];
        return [
          {
            label: descriptor.label,
            value: String(value),
            isLink: descriptor.isLink,
          },
        ];
      }
      case 'number': {
        const value = item[descriptor.key];
        if (value === undefined || value === null) return [];
        return [{ label: descriptor.label, value: value as number }];
      }
      case 'date': {
        const value = item[descriptor.key];
        if (!value) return [];
        return [{ label: descriptor.label, value: formatDate(String(value)) }];
      }
      case 'boolean': {
        const value = item[descriptor.key];
        if (value === undefined || value === null) return [];
        return [{ label: descriptor.label, value: value ? 'true' : 'false' }];
      }
      case 'array': {
        const arr = item[descriptor.key] as string[] | undefined;
        const formatted = arr?.length ? arr.join(', ') : '';
        if (!formatted) return [];
        return [
          {
            label: descriptor.label,
            value: formatted,
            isLink: descriptor.isLink,
          },
        ];
      }
      default:
        return [];
    }
  });
